import { ApiError, isConnectivityError } from '../api/client';
import type { PersonAttributes } from '../api/types';
import { STEWARD_ROLES } from '../api/types';
import { personIsLinked, personRelationshipIsSelf } from './personRecord';

/**
 * The person self link (steward-app#1851): what one Family record offers the person looking
 * at it, as a pure decision, and every string those controls show.
 *
 * What appcore stores in `linkedUserId` says which login a household record IS. It is written
 * only by appcore's self-link routes, always with the caller's own user id: nothing in the
 * SPA or the façade names a user. The SPA never receives that id. A row carries a fixed
 * marker when it is linked, and which record is the viewer's own comes from
 * `GET /people/self`.
 *
 * ⚠️ **Display only.** Nothing here is an access decision and nothing may become one: the
 * link grants nothing and clearing it revokes nothing (docs/04-navigation-ia.md §3.3). Every
 * rule below is a second copy of a gate appcore enforces (`handlers/people.ts` there), kept
 * so the page does not offer a button that would be refused. Hiding a control here protects
 * nothing on its own.
 */

/** Every user-visible string the self link adds. One place, so copy review reads one file. */
export const SELF_LINK_COPY = {
  claim: 'This is me',
  claiming: 'Saving…',
  own: 'You',
  unlink: 'This is not me',
  detailOwn: 'This record is you.',
  detailClaim: 'Is this your own record? You can mark it as yours.',
  errLinkedToAnother: 'Someone else has already marked this record as theirs.',
  errSelfRecordTaken: 'You have already marked another record here as yours. Clear that one first.',
  errAdvisor: 'Advisors are not part of the family they advise, so this record cannot be marked as yours.',
  errGone: 'We could not find that record. It may have been removed.',
  errOffline: "We could not reach Steward. Check your connection and try again.",
  errGeneric: 'We could not save that. Please try again.',
  // An admin clearing somebody else's link (steward-app#1988).
  clear: 'Clear link',
  clearTitle: "Clear this record's link?",
  clearBodyBefore: 'The record for ',
  clearBodyAfter: " will no longer be marked as belonging to anyone's sign-in. Nobody loses access, and nothing else on the record changes. A family member who has not marked a record as theirs can then mark this one.",
  clearConfirm: 'Clear link',
  clearCancel: 'Keep it',
  errClearRefused: 'You do not have permission to clear this link.',
} as const;

/**
 * The sentence for a refused link or unlink. Mapped by code and never the server's own
 * message, which is written for an engineer. An unrecognised refusal gets the generic line.
 */
export function selfLinkErrorMessage(err: unknown): string {
  if (isConnectivityError(err)) return SELF_LINK_COPY.errOffline;
  if (err instanceof ApiError) {
    if (err.code === 'PERSON_LINKED_TO_ANOTHER') return SELF_LINK_COPY.errLinkedToAnother;
    if (err.code === 'SELF_RECORD_TAKEN') return SELF_LINK_COPY.errSelfRecordTaken;
    if (err.code === 'ADVISOR_CANNOT_LINK_SELF') return SELF_LINK_COPY.errAdvisor;
    if (err.status === 404) return SELF_LINK_COPY.errGone;
  }
  return SELF_LINK_COPY.errGeneric;
}

/**
 * The sentence for a refused admin clear (steward-app#1988). Its own mapping, because the
 * self-link sentences are about "yours": appcore answers an advisor with
 * `ADVISOR_CANNOT_LINK_SELF` here too, and "cannot be marked as yours" would be the wrong
 * thing to say to someone clearing another person's link. Every 403 gets the one refusal.
 */
export function clearLinkErrorMessage(err: unknown): string {
  if (isConnectivityError(err)) return SELF_LINK_COPY.errOffline;
  if (err instanceof ApiError) {
    if (err.status === 403) return SELF_LINK_COPY.errClearRefused;
    if (err.status === 404) return SELF_LINK_COPY.errGone;
  }
  return SELF_LINK_COPY.errGeneric;
}

/** Whether a membership is an advisor connection: it carries the base advisor role. Every
 *  role set `connect` mints starts with it (the façade's `advisor.test.ts` pins
 *  `DEFAULT_CONNECT_ROLE_IDS` against the invite it creates). A membership that is BOTH an
 *  advisor and a workspace admin (steward-app#1087) counts as an advisor here. */
export function isAdvisorMembership(roleIds: readonly string[] | undefined): boolean {
  return (roleIds ?? []).includes(STEWARD_ROLES.ADVISOR);
}

export interface SelfLinkViewer {
  /**
   * The id of the viewer's own person record in this workspace, from `GET /people/self`:
   * a string when they have one, `null` when the route has answered that they have none,
   * and `undefined` while that read is loading or has failed. `undefined` is "not known",
   * and nothing that depends on the answer is offered until it is known.
   */
  selfPersonId: string | null | undefined;
  isAdvisor: boolean;
  isAdmin: boolean;
  /** Whether the roster read has succeeded; a caller with an unsettled roster passes false. */
  rosterKnown: boolean;
}

export interface SelfLinkRowState {
  /** This record is the viewer's own. */
  own: boolean;
  /** Offer "This is me". */
  canClaim: boolean;
  /** Offer "This is not me" (only ever on the viewer's own record). */
  canUnlink: boolean;
  /** Offer "Clear link": an admin clearing a link that is somebody else's. */
  canClear: boolean;
  /** The admin's link to People → Access, or null for none. */
  signpost: 'manage' | 'invite' | null;
}

/**
 * What one record offers this viewer.
 *
 * Two facts feed it and they come from different places. WHOSE record is the viewer's comes
 * from `GET /people/self` (`viewer.selfPersonId`). WHETHER a record is linked to a login
 * comes from the marker on the row (`personIsLinked`). No user id is compared here: person
 * rows do not carry one.
 *
 * - **own**: the self read has answered with this record's id.
 * - **canClaim**: the record is linked to nobody, the self read has answered that the viewer
 *   has no record (`null`), the roster is loaded, and the viewer is not an advisor. A
 *   viewer-only member may claim: appcore asks for read access to the record, not write.
 * - **canUnlink**: the viewer's own record.
 * - **canClear** (steward-app#1988): the record is linked, the self read has answered and
 *   this record is not the viewer's own, the roster is loaded, and the viewer is an admin
 *   who is not an advisor. appcore's rule is "a `mem:manage` holder who is not an advisor in
 *   this workspace"; the SPA does not read capabilities here and uses the admin role set as
 *   its stand-in (`useIsAdmin`, from the membership `/me` returns), so the two can disagree
 *   and appcore's 403 is the answer when they do.
 * - **signpost** (admins only): `manage` on a record linked to someone else; `invite` on a
 *   record linked to nobody. Never on the viewer's own record. One fallback survives from
 *   steward-app#1977: while the viewer has NO record of their own, an unlinked record whose
 *   relationship reads "Self" gets no invite, because before anyone has claimed anything
 *   that record is most likely the admin's own and inviting yourself is the dead end #1977
 *   removed. Once the viewer has a record the fallback is off: "Self" on some other record
 *   is then somebody else's word for themselves.
 *
 * While the self read is not known (`undefined`): no claim on any record; no signpost on a
 * linked record (it may be the viewer's own); no invite on an unlinked "Self" record (the
 * fallback cannot be decided). An unlinked record with any other relationship keeps its
 * invite, which does not depend on the answer.
 */
export function selfLinkRowState(personId: string, attributes: PersonAttributes, viewer: SelfLinkViewer): SelfLinkRowState {
  const linked = personIsLinked(attributes);
  const selfKnown = viewer.selfPersonId !== undefined;
  const hasOwnRecord = typeof viewer.selfPersonId === 'string';
  const own = hasOwnRecord && personId === viewer.selfPersonId;
  const canClaim = !linked && !viewer.isAdvisor && viewer.selfPersonId === null && viewer.rosterKnown;
  let signpost: SelfLinkRowState['signpost'] = null;
  if (viewer.isAdmin && !own) {
    if (linked) signpost = selfKnown ? 'manage' : null;
    else if (!personRelationshipIsSelf(attributes) || hasOwnRecord) signpost = 'invite';
  }
  const canClear = linked && selfKnown && !own && viewer.rosterKnown && viewer.isAdmin && !viewer.isAdvisor;
  return { own, canClaim, canUnlink: own, canClear, signpost };
}
